import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtUsd, fmtSigned } from "@/lib/journal";
import { BellSimple, Trash } from "@phosphor-icons/react";

export const AlertHistoryDialog = ({ open, onOpenChange }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/alerts/history", { params: { limit: 50 } })
      .then(({ data }) => setAlerts(data.alerts))
      .catch(() => toast.error("Failed to load alert history"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open]);

  const clear = async () => {
    try {
      const { data } = await api.delete("/alerts/history");
      toast.success(`Cleared ${data.deleted} alerts`);
      load();
    } catch {
      toast.error("Failed to clear history");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="alert-history-dialog" className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Alert History <span className="text-[#C58361]">/ Fired</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Every price move that triggered an email, newest first.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {loading ? (
            <p className="font-mono text-sm text-zinc-600">Loading history…</p>
          ) : alerts.length === 0 ? (
            <div className="border border-dashed border-[#1F1F1F] px-4 py-12 text-center" data-testid="alert-history-empty">
              <BellSimple size={20} className="mx-auto text-[#C58361]" weight="light" />
              <p className="mt-4 text-sm text-zinc-500">No alerts have fired yet.</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-[#1F1F1F] border border-[#1F1F1F]">
                {alerts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`alert-row-${a.id}`}>
                    <div>
                      <p className="font-mono text-sm text-white">
                        {a.asset || "—"}
                        <span className={`ml-2 text-xs ${a.change_pct >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
                          {a.change_pct >= 0 ? "+" : ""}{a.change_pct}%
                        </span>
                      </p>
                      <p className="mt-0.5 font-mono text-[10px] text-zinc-600">
                        {a.portfolio || (a.asset ? "—" : "legacy alert row")} · {(a.sent_at || "").slice(0, 16).replace("T", " ")} UTC
                        {a.threshold_pct ? ` · threshold ${a.threshold_pct}%` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs text-zinc-300">
                        {a.entry_price != null ? fmtUsd(a.entry_price, 4) : "—"} → {a.mark_price != null ? fmtUsd(a.mark_price, 4) : "—"}
                      </p>
                      {a.unrealized != null && (
                        <p className={`font-mono text-[10px] ${a.unrealized >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
                          {fmtSigned(a.unrealized)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button
                data-testid="alert-history-clear-btn"
                onClick={clear}
                className="w-full rounded-none border border-[#1F1F1F] bg-transparent py-5 text-xs text-zinc-400 hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
              >
                <Trash size={13} className="mr-2" /> Clear History
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
